import { Link } from "react-router-dom";
import { DOCTOR } from "../../lib/clinic";
import SafeImage from "./SafeImage";

// Two canonical author profiles used across blog + service pages.
const AUTHORS = {
  shantanu: {
    slug: "shantanu",
    name: "Dr. Shantanu Kumar Gupta",
    role: "Consultant Ophthalmologist — Cataract, Cornea & Medical Retina",
    credentials: DOCTOR.qualificationsCompact,
    portrait: DOCTOR.portrait,
    href: "/about",
    bio:
      "Consultant Ophthalmologist at Shantanu Netralaya Eye Hospital, Varanasi. New Delhi (MAMC / Guru Nanak Eye Centre) trained cataract, cornea and medical retina specialist. FICO (Merit).",
  },
  manisha: {
    slug: "manisha",
    name: "Dr. Manisha Mishra",
    role: "Consultant Ophthalmologist — Complex Cataract, Squint & Pediatric Ophthalmology",
    credentials: "MBBS (Gold Medalist) · MS (MAMC) · DNB · FICO (Merit)",
    portrait:
      "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4ykhz87v_F43F41D7-9C9C-464E-90EE-6356CEF829FD.png",
    href: "/team/dr-manisha-mishra",
    bio:
      "Consultant Ophthalmologist at Shantanu Netralaya Eye Hospital, Varanasi. MBBS Gold Medalist with 4 distinctions; MS Ophthalmology from Maulana Azad Medical College & Guru Nanak Eye Centre, New Delhi. Handles complex cataract, squint (strabismus) and paediatric ophthalmology.",
  },
};

// Mirror of lib/schema.js `authorForPost` — kept in sync so on-screen
// byline and JSON-LD author never disagree.
export const resolveAuthor = (post) => {
  if (!post) return AUTHORS.shantanu;
  const explicit = (post.author_slug || "").toLowerCase();
  if (explicit === "manisha") return AUTHORS.manisha;
  if (explicit === "shantanu") return AUTHORS.shantanu;
  const haystack = [
    (post.category || "").toLowerCase(),
    ...(post.tags || []).map((t) => (t || "").toLowerCase()),
    (post.title || "").toLowerCase(),
    (post.slug || "").toLowerCase(),
  ].join(" ");
  if (/(squint|strabismus|paediatric|pediatric|amblyop|bhengapan|manisha|complex cataract|complicated cataract)/i.test(haystack)) {
    return AUTHORS.manisha;
  }
  return AUTHORS.shantanu;
};

/**
 * AuthorCard — visible author box on blog posts (Google helpful-content
 * signal).  Accepts either a `post` object or a `doctorSlug` prop.
 * Backwards-compatible: renders Dr. Shantanu when called with no props.
 */
const AuthorCard = ({ post = null, doctorSlug = null } = {}) => {
  const a = doctorSlug && AUTHORS[doctorSlug]
    ? AUTHORS[doctorSlug]
    : post
    ? resolveAuthor(post)
    : AUTHORS.shantanu;

  return (
    <div
      className="not-prose mt-14 rounded-2xl border border-border p-6 sm:p-7 bg-[hsl(var(--secondary))]/40 flex flex-col sm:flex-row gap-6"
      data-testid={`author-card-${a.slug}`}
    >
      <div className="w-24 h-24 shrink-0 rounded-full overflow-hidden border border-border">
        <SafeImage src={a.portrait} alt={a.name} label={a.name} className="w-full h-full" loading="lazy" />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-1">
          Written &amp; medically reviewed by
        </div>
        <div className="font-serif text-2xl">{a.name}</div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">{a.credentials}</div>
        <p className="mt-3 text-sm text-[hsl(var(--foreground))]/80 leading-relaxed">{a.bio}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to={a.href}
            className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]"
            data-testid={`author-more-${a.slug}`}
          >
            More about {a.name.split(" ").slice(0, 2).join(" ")} →
          </Link>
          <Link
            to="/appointment"
            className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]"
            data-testid={`author-book-${a.slug}`}
          >
            Book a consultation →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthorCard;
